import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ApplicationLayout from 'v3/layouts/Application';
import { validatePermission } from 'actions';
import { Button, Card, Text } from 'v3/components';
import Space from 'v3/components/Space';
import { useModal } from 'hooks';
import { cooperplaceApi } from 'services/api';
import { useChecklist } from '../shared/context';
import {
  BranchOfficesInfo,
  ClientInfo,
  ClientsInfo,
  GeneralInfo,
  ImplementsInfo,
  QuestionsSection,
  VehiclesBodyInfo,
  VehiclesInfo,
} from './components';
import { AuditModal } from './AuditModal';
import { ChecklistData } from '../shared/Data';
import { CheckListSchema } from '../components/RegisterComponents/validator';

export function Details() {
  const params = useParams();
  const navigate = useNavigate();
  const checklist = useChecklist();
  const hasPermissionToView = validatePermission('VISUALIZAR_CHECKLIST');
  const hasPermissionToEdit = validatePermission('CADASTRAR_CHECKLIST');
  const auditModal = useModal();

  const processedLogs = checklist?.data?.checkListLogs?.map(log => ({
    ...log,
    show: false,
  }));

  const [editing, setEditing] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);

  const CHECKLIST_TYPE_SINGLE_SLUG = 'SeparateChecklist';
  const isSingleChecklist =
    (checklist?.data?.checklistType?.slug || '') === CHECKLIST_TYPE_SINGLE_SLUG;

  function isTrainingChecklistType(checklistType) {
    const slug = String(checklistType?.slug || '').toLowerCase();
    const name = String(checklistType?.name || checklistType?.label || '')
      .trim()
      .toLowerCase();

    return slug === 'training' || name === 'treinamento';
  }

  function validateTrainingChecklist(data, questions) {
    const errorList = {};
    const workload = String(data?.courseWorkload || '').trim();

    if (!String(data?.name || '').trim()) {
      errorList.name = 'Nome obrigatório';
    }

    if (!data?.checklistType?.id) {
      errorList.type = 'Tipo obrigatório';
    }

    if (!String(data?.description || '').trim()) {
      errorList.description = 'Descrição obrigatória';
    }

    if (!workload) {
      errorList.courseWorkload = 'Duração do treinamento obrigatória';
    } else if (!/^\d{2}:[0-5]\d$/.test(workload) || workload === '00:00') {
      errorList.courseWorkload = 'Informe a duração no formato HH:mm';
    }

    if (!Array.isArray(questions) || questions.length === 0) {
      errorList.questions = 'Inclua ao menos uma pergunta';
    }

    checklist.setErrors(errorList);

    return Object.keys(errorList).length === 0;
  }

  useEffect(() => {
    if (!hasPermissionToView) {
      checklist.showSnackbar(
        'Você não tem permissão para visualizar checklists',
        { type: 'error' }
      );
      navigate('/');
    }
  }, [hasPermissionToView]);

  function handleCancel() {
    checklist.handleCancel();
    setEditing(false);
  }

  async function validate(data, setErrors) {
    const errorList = {};
    const dataObject = {
      name: data?.checklistInfo?.name,
      description: data?.checklistInfo?.description,
      type: data?.checklistInfo?.checklistType?.id,
      questions: data?.questions?.map(item => ({
        checklist_id: item?.id,
        order: item?.order,
        required: item?.required,
      })),
      clients: data?.checklistInfo?.clients,
      negotiation: data?.checklistInfo?.negotiation,
      vehicleType: data?.checklistInfo?.vehicleType,
      vehicleBodyType: data?.checklistInfo?.vehicleBodyType,
      implementType: data?.checklistInfo?.implementType,
      branchOffices: data?.checklistInfo?.branchOffices,
      hasDollyImplement: data?.checklistInfo?.hasDollyImplement,
      defaultCostCenter: data?.checklistInfo?.defaultCostCenter,
      costCentersId: data?.checklistInfo?.costCentersId,
    };

    try {
      await CheckListSchema.validate(dataObject, {
        abortEarly: false,
      });
      setErrors({ ...errorList });
      return true;
    } catch (err) {
      err.inner.forEach(error => {
        errorList[error.path] = error.message;
      });
      setErrors({ ...errorList });
      return false;
    }
  }

  async function handleUpdateChecklist(questions) {
    if (loadingEdit) return;

    try {
      setLoadingEdit(true);

      const isTrainingChecklist = isTrainingChecklistType(
        checklist?.data?.checklistType
      );

      if (isTrainingChecklist) {
        const isTrainingDataValid = validateTrainingChecklist(
          checklist?.data,
          questions
        );

        if (!isTrainingDataValid) {
          checklist.showSnackbar('Revise os campos obrigatórios.', {
            type: 'error',
          });
          setLoadingEdit(false);
          return;
        }
      }

      if (isSingleChecklist) {
        const payload = {
          active: !!checklist?.data?.active,
          appVisible: !!checklist?.data?.appVisible,
          name: checklist?.data?.name,
          description: checklist?.data?.description,
          checkTypeId: checklist?.data?.checklistType?.id,

          expirationDays: Number(checklist?.data?.singleDays || 0),
          groupId: (checklist?.data?.groups || []).map(g => g.id),

          questions: (questions || []).map(q => ({
            questionId: q?.id,
            order: q?.order,
            required: q?.required === true || q?.required === 'true',
          })),

          default: false,
          defaultCostCenter: false,
          require: false,
          cnpjClientsId: [],
          availableForDriver: false,
          branchOffices: [],
          requiredDriverSign: false,
          requiredInspectSign: false,
          availableLogisticPanel: false,
        };

        await cooperplaceApi.put(`checklists/${params?.id}`, payload);
        await checklist.fetchChecklist(params?.id);

        checklist.showSnackbar('Checklist avulso atualizado com sucesso!', {
          type: 'success',
        });

        setTimeout(() => {
          setLoadingEdit(false);
          setEditing(false);
        }, 500);

        return;
      }

      if (!isTrainingChecklist) {
        const hasDollyImplement = !!checklist?.data?.implementType?.find(
          ({ name }) => name === 'Dolly'
        );
        const dataToValidate = { ...checklist?.data, hasDollyImplement };

        const isDataValid = await validate(
          { checklistInfo: dataToValidate, questions },
          checklist.setErrors
        );

        if (!isDataValid) {
          setLoadingEdit(false);
          return;
        }
      }

      const payload = isTrainingChecklist
        ? {
            require: false,
            default: true,
            active: !!checklist?.data?.active,
            name: checklist?.data?.name,
            description: checklist?.data?.description,
            courseWorkload: checklist?.data?.courseWorkload,
            checkTypeId: checklist?.data?.checklistType?.id,
            cnpjClientsId: [],
            negotiationId: null,
            vehiclesTypesId: [],
            vehiclesBodyId: [],
            vehiclesImplementsId: [],
            questions: (questions || []).map(question => ({
              questionId: question?.id,
              required: true,
              order: question?.order,
            })),
            availableForDriver: false,
            branchOffices: [],
            requiredDriverSign: false,
            requiredInspectSign: false,
            availableLogisticPanel: false,
            defaultCostCenter: false,
            costCentersId: [],
            serviceLevels: [],
          }
        : (() => {
            const serviceLevelsPayload = (checklist.data.serviceLevels || []).map(sl => {
              const raw = sl.serviceLevel ?? sl.slug;
              const backendSlug = raw === 'BRONZE' ? 'AGREGADO' : raw;
              return {
                serviceLevel: backendSlug,
                days: sl.days ?? sl.checklistDays ?? 0,
                quantity: sl.quantity ?? sl.travelQuantity ?? 0,
                condition: sl.condition ?? (sl.orAnd ? 'OR' : 'AND'),
              };
            });

            return {
              require: checklist?.data?.required || false,
              default: checklist?.data?.default || false,
              active: checklist?.data?.active || false,
              name: checklist?.data?.name,
              description: checklist?.data?.description,
              checkTypeId: checklist?.data?.checklistType?.id,
              cnpjClientsId: checklist?.data?.clients?.map(item => item?.id),
              negotiationId: checklist?.data?.negotiation?.id,
              vehiclesTypesId: checklist?.data?.vehicleType?.map(item => item?.id),
              vehiclesBodyId: checklist?.data?.vehicleBodyType?.map(item => item?.id),
              vehiclesImplementsId: checklist?.data?.implementType?.map(item => item?.id),

              questions: (questions || []).map(question => ({
                questionId: question?.id,
                required: question?.required === true || question?.required === 'true',
                order: question?.order,
              })),

              availableForDriver: !!checklist?.data?.availableForDriver,
              branchOffices: (checklist?.data?.branchOffices || []).map(item => item?.id),
              requiredDriverSign: !!checklist?.data?.requiredDriverSign,
              requiredInspectSign: !!checklist?.data?.requiredInspectSign,
              availableLogisticPanel: !!checklist?.data?.availableLogisticPanel,
              defaultCostCenter: !!checklist.data.defaultCostCenter,
              costCentersId: checklist.data.costCentersId || [],
              serviceLevels: serviceLevelsPayload,
            };
          })();

      await cooperplaceApi.put(`checklists/${params?.id}`, payload);
      await checklist.fetchChecklist(params?.id);

      checklist.showSnackbar('Checklist atualizado com sucesso!', {
        type: 'success',
      });

      setTimeout(() => {
        setLoadingEdit(false);
        setEditing(false);
      }, 500);
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        'Não foi possível atualizar o checklist';
      checklist.showSnackbar(`${message}`, {
        type: 'error',
      });
      checklist.handleCancel();
      setLoadingEdit(false);
    }
  }

  return (
    <ApplicationLayout
      title="Checklist"
      RightComponent={
        !editing && (
          <Space x="0.5rem">
            <Button
              variant="secondary"
              onClick={() => navigate(-1)}
              disabled={checklist.loading}
            >
              <Text type="regular" weight={500}>
                Voltar
              </Text>
            </Button>
            <Button
              onClick={() => navigate('/checklist/cadastrar')}
              disabled={checklist.loading}
            >
              <Text type="regular" weight={500}>
                Novo checklist
              </Text>
            </Button>
          </Space>
        )
      }
    >
      {editing ? (
        <ChecklistData
          handleSave={handleUpdateChecklist}
          isLoading={loadingEdit}
          handleCancel={handleCancel}
        />
      ) : (
        <>
          <Card
            header={
              <Text color="dark" type="header">
                {`Checklist ${checklist?.data?.id}`}
              </Text>
            }
            HeaderRightComponent={
              <div style={{ display: 'flex', gap: 8 }}>
                <Button onClick={() => auditModal.open()}>
                  <Text type="regular" weight={500}>
                    Auditoria
                  </Text>
                </Button>
                {hasPermissionToEdit && (
                  <Button
                    variant="secondary"
                    weight={500}
                    loading={checklist.loading}
                    onClick={() => setEditing(true)}
                  >
                    <Text type="regular">Editar</Text>
                  </Button>
                )}
              </div>
            }
            loading={checklist?.loading}
          >
            <GeneralInfo checklist={checklist} />

            {!isSingleChecklist && checklist?.data?.branchOffices?.length > 0 && (
              <BranchOfficesInfo checklist={checklist} />
            )}

            {!isSingleChecklist && checklist?.data?.clients?.length === 1 && (
              <ClientInfo checklist={checklist} />
            )}

            {!isSingleChecklist && checklist?.data?.clients?.length > 1 && (
              <ClientsInfo checklist={checklist} />
            )}

            {!isSingleChecklist && checklist?.data?.vehicleType?.length > 0 && (
              <VehiclesInfo checklist={checklist} />
            )}

            {!isSingleChecklist && checklist?.data?.vehicleBodyType?.length > 0 && (
              <VehiclesBodyInfo checklist={checklist} />
            )}

            {!isSingleChecklist && checklist?.data?.implementType?.length > 0 && (
              <ImplementsInfo checklist={checklist} />
            )}

            <QuestionsSection checklist={checklist} />
          </Card>

          <AuditModal
            isOpen={auditModal.isOpen}
            onClose={() => auditModal.close()}
            logs={processedLogs}
          />
        </>
      )}
    </ApplicationLayout>
  );
}
